export type GalleryTheme = "computer" | "textile" | "holapur" | "community";

export interface GalleryImage {
  id: string;
  src: string;
  caption: string;
  theme: GalleryTheme;
  aspect: "portrait" | "landscape";
  /** Part of the contact-sheet intro that settles into the grid on load. */
  featured?: boolean;
}

export interface GalleryThemeMeta {
  id: GalleryTheme;
  /** Short chip label. */
  label: string;
  /** Longer heading used above the theme's photographs. */
  heading: string;
}

/**
 * Theme metadata. Order defines the on-page reading order and the filter chips
 * (an "All" chip is prepended in the UI).
 */
export const galleryThemes: GalleryThemeMeta[] = [
  {
    id: "computer",
    label: "The computer room",
    heading: "The computer room",
  },
  {
    id: "textile",
    label: "Hand & textile",
    heading: "Hand & textile",
  },
  {
    id: "holapur",
    label: "The Holapur centre",
    heading: "The Holapur centre",
  },
  {
    id: "community",
    label: "Community & courtyard",
    heading: "Community & courtyard",
  },
];

/**
 * Photographs from both centres — Keshripur (Varanasi) and the newly opened
 * Holapur centre. Computer-training and Holapur coverage was added in July 2026
 * at the trustees' request.
 */
export const galleryImages: GalleryImage[] = [
  // The computer room ---------------------------------------------------------
  {
    id: "holapur-computer-01",
    src: "/gallery/holapur-computer-01.jpg",
    caption: "At the keyboard in the Holapur computer room",
    theme: "computer",
    aspect: "landscape",
    featured: true,
  },
  {
    id: "holapur-computer-02",
    src: "/gallery/holapur-computer-02.jpg",
    caption: "Working through the day's exercise, screen by screen",
    theme: "computer",
    aspect: "landscape",
  },
  {
    id: "holapur-computer-03",
    src: "/gallery/holapur-computer-03.jpg",
    caption: "Two students share a monitor at Holapur",
    theme: "computer",
    aspect: "landscape",
  },
  {
    id: "keshripur-computer-01",
    src: "/gallery/keshripur-computer-01.jpg",
    caption: "Focused at the monitor in the Varanasi computer lab",
    theme: "computer",
    aspect: "landscape",
  },
  {
    id: "keshripur-computer-02",
    src: "/gallery/keshripur-computer-02.jpg",
    caption: "A full computer class in session at Varanasi",
    theme: "computer",
    aspect: "landscape",
    featured: true,
  },
  {
    id: "keshripur-computer-03",
    src: "/gallery/keshripur-computer-03.jpg",
    caption: "Side by side at the keyboards in the Varanasi lab",
    theme: "computer",
    aspect: "landscape",
  },

  // Hand & textile ------------------------------------------------------------
  {
    id: "keshripur-sewing-01",
    src: "/gallery/keshripur-sewing-01.jpg",
    caption: "At the Punam treadle machine in the Varanasi centre",
    theme: "textile",
    aspect: "landscape",
    featured: true,
  },
  {
    id: "keshripur-sewing-02",
    src: "/gallery/keshripur-sewing-02.jpg",
    caption: "Two students working their machines side by side",
    theme: "textile",
    aspect: "landscape",
  },
  {
    id: "keshripur-sewing-03",
    src: "/gallery/keshripur-sewing-03.jpg",
    caption: "Guiding cloth through the needle",
    theme: "textile",
    aspect: "landscape",
  },
  {
    id: "keshripur-sewing-04",
    src: "/gallery/keshripur-sewing-04.jpg",
    caption: "Steady hands at the sewing machine",
    theme: "textile",
    aspect: "landscape",
  },
  {
    id: "keshripur-cutting-01",
    src: "/gallery/keshripur-cutting-01.jpg",
    caption: "Laying out and cutting fabric together",
    theme: "textile",
    aspect: "landscape",
  },

  // The Holapur centre --------------------------------------------------------
  {
    id: "holapur-building",
    src: "/gallery/holapur-building.jpg",
    caption: "The Holapur Skills Centre — our newest centre in Varanasi",
    theme: "holapur",
    aspect: "portrait",
    featured: true,
  },
  {
    id: "holapur-banner-close",
    src: "/gallery/holapur-banner-close.jpg",
    caption:
      "The Sadafal Education Skills Centre banner on the Holapur building",
    theme: "holapur",
    aspect: "landscape",
  },
  {
    id: "holapur-course-banner",
    src: "/gallery/holapur-course-banner.jpg",
    caption: "Reading through the courses on offer at Holapur",
    theme: "holapur",
    aspect: "landscape",
  },
  {
    id: "holapur-entrance-group",
    src: "/gallery/holapur-entrance-group.jpg",
    caption: "Students at the entrance to the Holapur centre",
    theme: "holapur",
    aspect: "landscape",
  },
  {
    id: "holapur-group",
    src: "/gallery/holapur-group.jpg",
    caption: "A morning's group at the Holapur centre",
    theme: "holapur",
    aspect: "landscape",
  },

  // Community & courtyard -----------------------------------------------------
  {
    id: "holapur-lawn-01",
    src: "/gallery/holapur-lawn-01.jpg",
    caption: "Studying together on the lawn at Holapur",
    theme: "community",
    aspect: "landscape",
    featured: true,
  },
  {
    id: "holapur-lawn-02",
    src: "/gallery/holapur-lawn-02.jpg",
    caption: "A laptop shared in the open air",
    theme: "community",
    aspect: "landscape",
  },
  {
    id: "holapur-lawn-03",
    src: "/gallery/holapur-lawn-03.jpg",
    caption: "The class gathers on the grass at Holapur",
    theme: "community",
    aspect: "landscape",
  },
  {
    id: "keshripur-group-01",
    src: "/gallery/keshripur-group-01.jpg",
    caption: "Working together on a shared mat in Varanasi",
    theme: "community",
    aspect: "landscape",
  },
  {
    id: "keshripur-group-02",
    src: "/gallery/keshripur-group-02.jpg",
    caption: "The circle at work in the Varanasi courtyard",
    theme: "community",
    aspect: "landscape",
  },
];
